import { InputHTMLAttributes, forwardRef, useEffect, useState, useRef, useCallback, ChangeEvent } from 'react';
import { Calendar } from 'lucide-react';

function isoToBr(iso: string): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  const [, y, mo, d] = m;
  return `${d}/${mo}/${y}`;
}

function brToIso(br: string): string | null {
  const m = br.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const [, d, mo, y] = m;
  const day = +d;
  const month = +mo;
  const year = +y;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  if (year < 1900 || year > 2100) return null;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

function maskBr(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  const len = digits.length;
  if (len <= 2) return digits;
  if (len <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function parseAnyFormat(text: string): string | null {
  const trimmed = text.trim();

  // YYYY-MM-DD
  const iso = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) {
    const [, y, mo, d] = iso;
    return brToIso(`${d.padStart(2, '0')}/${mo.padStart(2, '0')}/${y}`);
  }

  // DD/MM/YYYY or DD-MM-YYYY or DD.MM.YYYY
  const br = trimmed.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
  if (br) {
    const [, d, mo, y] = br;
    return brToIso(`${d.padStart(2, '0')}/${mo.padStart(2, '0')}/${y}`);
  }

  // 8 digits like 09021973 → 09/02/1973
  const digits = trimmed.match(/^(\d{8})$/);
  if (digits) {
    const v = digits[1];
    return brToIso(`${v.slice(0, 2)}/${v.slice(2, 4)}/${v.slice(4)}`);
  }

  return null;
}

export interface DateInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  label?: string;
  error?: string;
  value: string; // YYYY-MM-DD or empty
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(({
  className = '',
  label,
  error,
  id,
  value,
  onChange,
  name,
  ...props
}, ref) => {
  const inputId = id || Array.from({ length: 8 }, () => Math.random().toString(36)[2]).join('');
  const [display, setDisplay] = useState(() => isoToBr(value));
  const pickerRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDisplay(isoToBr(value));
  }, [value]);

  const fireChange = useCallback((newIso: string) => {
    if (!onChange) return;
    const synthetic = {
      target: { value: newIso, name: name || '' },
      currentTarget: { value: newIso, name: name || '' },
    } as ChangeEvent<HTMLInputElement>;
    onChange(synthetic);
  }, [onChange, name]);

  const commitFromText = useCallback((text: string) => {
    const direct = parseAnyFormat(text);
    if (direct !== null) {
      setDisplay(isoToBr(direct));
      fireChange(direct);
      return;
    }
    const masked = maskBr(text);
    setDisplay(masked);
    const iso = brToIso(masked);
    if (iso) {
      fireChange(iso);
    } else if (masked === '') {
      fireChange('');
    }
  }, [fireChange]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    commitFromText(e.target.value);
  };

  const handlePickerChange = (e: ChangeEvent<HTMLInputElement>) => {
    const iso = e.target.value;
    setDisplay(isoToBr(iso));
    fireChange(iso);
  };

  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    if (typeof (el as HTMLInputElement & { showPicker?: () => void }).showPicker === 'function') {
      (el as HTMLInputElement & { showPicker: () => void }).showPicker();
    } else {
      el.focus();
      el.click();
    }
  };

  return (
    <div className="flex flex-col gap-1.5 w-full">
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium tracking-wide uppercase text-on-surface-variant">
          {label}
        </label>
      )}
      <div className="relative w-full">
        <input
          id={inputId}
          ref={ref}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="DD/MM/AAAA"
          value={display}
          onChange={handleChange}
          name={name}
          className={`w-full bg-surface-container-high text-on-surface placeholder-on-surface-variant/50 px-4 py-2.5 pr-10 rounded
            focus:outline-none focus:ring-1 focus:ring-primary focus:shadow-[0_0_8px_rgba(180,197,255,0.2)] transition-shadow
            ${error ? 'bg-error-container/10 border-error text-error' : 'border border-transparent'}
            ${className}`}
          {...props}
        />
        <button
          type="button"
          onClick={openPicker}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-on-surface/10 text-on-surface-variant"
          aria-label="Abrir calendário"
        >
          <Calendar className="w-4 h-4" />
        </button>
        <input
          ref={pickerRef}
          type="date"
          value={value || ''}
          onChange={handlePickerChange}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
        />
      </div>
      {error && <span className="text-xs text-error mt-0.5">{error}</span>}
    </div>
  );
});

DateInput.displayName = 'DateInput';
